import { useState, useEffect } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Database, HardDrive, Archive, Code, FileText, Upload, RefreshCw, Trash2, AlertTriangle, ChevronRight, ExternalLink, Loader2 } from 'lucide-react';

const StorageMonitor = () => {
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(null);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [selectedCleanup, setSelectedCleanup] = useState(null);
  const [depsOpen, setDepsOpen] = useState(false);
  const [depsData, setDepsData] = useState(null);
  const [depsLoading, setDepsLoading] = useState(false);
  const [depsActionId, setDepsActionId] = useState(null);

  useEffect(() => {
    loadStorageData();
  }, []);

  const openDepsModal = async () => {
    setDepsOpen(true);
    if (depsData) return;
    setDepsLoading(true);
    try {
      const r = await api.get(`${API}/system/storage-usage/deps-breakdown`);
      setDepsData(r.data);
    } catch (e) {
      console.error('deps-breakdown failed', e);
      toast.error('Failed to load dependencies breakdown');
    } finally {
      setDepsLoading(false);
    }
  };

  const runDepsAction = async (actionId, label) => {
    if (!window.confirm(`Run "${label}" now? This will modify files on disk.`)) return;
    setDepsActionId(actionId);
    try {
      const r = await api.post(`${API}/system/storage-usage/deps-action`, { action_id: actionId });
      if (r.data.mode === 'background' && r.data.job_id) {
        toast.info(`${label} started in background — polling for completion…`, { duration: 4000 });
        // Poll up to ~6 minutes (every 5s)
        let done = false;
        for (let i = 0; i < 72 && !done; i++) {
          await new Promise(res => setTimeout(res, 5000));
          try {
            const s = await api.get(`${API}/system/storage-usage/deps-action/status`, { params: { job_id: r.data.job_id } });
            if (s.data.state === 'success') {
              done = true;
              toast.success(`${label}: freed ${s.data.freed || '0 B'}`);
            } else if (s.data.state === 'failed') {
              done = true;
              toast.error(`${label} failed (exit ${s.data.return_code}). ${s.data.output_tail?.slice(-200) || ''}`);
            }
          } catch (pollErr) {
            console.warn('status poll error', pollErr);
          }
        }
        if (!done) {
          toast.warning(`${label} still running in background — refresh later to see results.`);
        }
      } else {
        // inline mode
        toast.success(`${label}: freed ${r.data.freed} (now ${r.data.disk_free_after} free)`);
      }
      setDepsData(null);
      await Promise.all([loadStorageData(), (async () => {
        const fresh = await api.get(`${API}/system/storage-usage/deps-breakdown`);
        setDepsData(fresh.data);
      })()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || `${label} failed`);
    } finally {
      setDepsActionId(null);
    }
  };

  const loadStorageData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${API}/system/storage-usage`);
      setStorageData(response.data);
    } catch (error) {
      console.error('Error loading storage data:', error);
      toast.error('Failed to load storage information');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async (action) => {
    setCleaning(action);
    try {
      const response = await api.post(`${API}/system/storage-cleanup?action=${action}`);
      if (response.data.success) {
        toast.success(response.data.message);
        await loadStorageData();
      } else {
        toast.error(response.data.message || 'Cleanup failed');
      }
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaning(null);
      setShowCleanupDialog(false);
      setSelectedCleanup(null);
    }
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'database': return <Database className="w-5 h-5" />;
      case 'code': return <Code className="w-5 h-5" />;
      case 'upload': return <Upload className="w-5 h-5" />;
      case 'archive': return <Archive className="w-5 h-5" />;
      case 'file-text': return <FileText className="w-5 h-5" />;
      default: return <HardDrive className="w-5 h-5" />;
    }
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-orange-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getCategoryColor = (index) => {
    const colors = ['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-orange-500', 'bg-pink-500'];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-orange-500" />
            <span className="text-gray-600">Calculating storage usage...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!storageData) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-gray-500">
            Failed to load storage data. Please try again.
            <Button onClick={loadStorageData} className="mt-4 bg-orange-600 hover:bg-orange-700">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Storage Usage Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-orange-500" />
            Storage Overview
          </CardTitle>
          <CardDescription>
            Your application storage usage breakdown
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Main progress bar */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">Total Usage</span>
              <span className="text-sm text-gray-500">
                {storageData.total_size} / {storageData.total_limit}
              </span>
            </div>
            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${getUsageColor(storageData.usage_percentage)}`}
                style={{ width: `${Math.min(storageData.usage_percentage, 100)}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-500">{storageData.usage_percentage}% used</span>
              <span className="text-xs text-gray-500">{storageData.remaining} remaining</span>
            </div>
          </div>

          {/* Category breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {storageData.categories?.map((category, index) => {
              const isDeps = category.name === 'Dependencies & Git';
              const Wrapper = isDeps ? 'button' : 'div';
              return (
                <Wrapper
                  key={category.name}
                  onClick={isDeps ? openDepsModal : undefined}
                  className={`p-4 border rounded-lg text-left w-full ${isDeps ? 'hover:border-orange-400 hover:shadow-md transition cursor-pointer' : ''}`}
                  data-testid={isDeps ? 'open-deps-breakdown-btn' : undefined}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg ${getCategoryColor(index)} text-white`}>
                      {getIcon(category.icon)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm flex items-center gap-1">
                        {category.name}
                        {isDeps && <ChevronRight className="w-4 h-4 text-gray-400" />}
                      </h4>
                      <p className="text-xs text-gray-500">{category.size}</p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getCategoryColor(index)}`}
                      style={{ width: `${category.percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {category.percentage}% of total{isDeps ? ' · click to see breakdown & how to reduce' : ''}
                  </p>
                </Wrapper>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Cleanup Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-500" />
            Storage Cleanup
          </CardTitle>
          <CardDescription>
            Free up space by removing unnecessary data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {storageData.cleanup_actions?.map((action) => (
              <div key={action.id} className="p-4 border rounded-lg">
                <h4 className="font-medium mb-1">{action.name}</h4>
                <p className="text-sm text-gray-500 mb-3">{action.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-orange-600">
                    Save: {action.estimated_savings}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedCleanup(action);
                      setShowCleanupDialog(true);
                    }}
                    disabled={cleaning === action.id}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    {cleaning === action.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dependencies & Git Breakdown Dialog */}
      <Dialog open={depsOpen} onOpenChange={setDepsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="deps-breakdown-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-orange-500" />
              Dependencies & Git — Breakdown
              {depsData?.environment && (
                <span
                  className={`ml-auto text-xs font-bold px-2 py-1 rounded-full border ${
                    depsData.environment.is_preview
                      ? 'bg-sky-50 text-sky-700 border-sky-300'
                      : 'bg-red-50 text-red-700 border-red-300'
                  }`}
                  data-testid="deps-env-badge"
                  title={depsData.environment.hostname}
                >
                  📍 {depsData.environment.name}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              What's inside the largest folders, and safe ways to shrink them.
            </DialogDescription>
          </DialogHeader>

          {depsData?.environment && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <div>
                <strong>These actions affect only THIS environment's filesystem.</strong> The
                database is never touched. Suggestions are recomputed live from the real disk —
                if a folder doesn't exist on a deployed server, the matching suggestion silently
                won't appear.
                {!depsData.environment.is_preview && (
                  <span className="block mt-1 font-semibold">
                    ⚠️ You are on the deployed Production environment — actions run against live disk.
                  </span>
                )}
              </div>
            </div>
          )}

          {depsLoading || !depsData ? (
            <div className="py-12 flex items-center justify-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" /> Calculating breakdown…
            </div>
          ) : (
            <div className="space-y-6 py-2">
              {/* node_modules section */}
              <div>
                <div className="flex justify-between items-baseline mb-2">
                  <h4 className="font-semibold text-sm text-gray-800">
                    node_modules <span className="text-gray-400 font-normal">/app/frontend/node_modules</span>
                  </h4>
                  <span className="text-sm font-bold text-orange-600">{depsData.node_modules.total_size}</span>
                </div>
                <div className="border rounded-lg divide-y" data-testid="deps-nm-top">
                  {depsData.node_modules.top_packages?.map((p) => (
                    <div key={p.name} className="px-3 py-2 text-sm flex justify-between items-center">
                      <div>
                        <span className="font-mono text-gray-800">{p.name}</span>
                        {p.children && (
                          <span className="ml-2 text-xs text-gray-400">
                            (top: {p.children.map(c => `${c.name} ${c.size}`).join(', ')})
                          </span>
                        )}
                      </div>
                      <span className="text-gray-600 font-medium">{p.size}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* git section */}
              <div>
                <div className="flex justify-between items-baseline mb-2">
                  <h4 className="font-semibold text-sm text-gray-800">
                    .git <span className="text-gray-400 font-normal">/app/.git</span>
                  </h4>
                  <span className="text-sm font-bold text-orange-600">{depsData.git.total_size}</span>
                </div>
                <div className="border rounded-lg divide-y" data-testid="deps-git-breakdown">
                  {depsData.git.breakdown?.map((g) => (
                    <div key={g.name} className="px-3 py-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="font-mono text-gray-800">{g.name}</span>
                        <span className="text-gray-600 font-medium">{g.size}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{g.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* suggestions */}
              <div>
                <h4 className="font-semibold text-sm text-gray-800 mb-2">How to reduce</h4>
                <div className="space-y-3" data-testid="deps-suggestions">
                  {depsData.suggestions?.map((s, i) => {
                    const impactStyles = {
                      high: 'border-emerald-300 bg-emerald-50',
                      medium: 'border-amber-300 bg-amber-50',
                      info: 'border-slate-200 bg-slate-50',
                    };
                    const impactLabel = {
                      high: 'High impact',
                      medium: 'Medium impact',
                      info: 'Info',
                    };
                    return (
                      <div key={i} className={`border-2 rounded-lg p-4 ${impactStyles[s.impact] || impactStyles.info}`}>
                        <div className="flex justify-between items-start gap-3 mb-1">
                          <h5 className="font-semibold text-sm text-gray-900">{s.title}</h5>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="text-xs font-bold px-2 py-0.5 rounded bg-white border">
                              {impactLabel[s.impact] || 'Info'}
                            </span>
                            {s.estimated_savings && s.estimated_savings !== '—' && (
                              <span className="text-xs font-bold text-emerald-700">~{s.estimated_savings}</span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-700">{s.description}</p>
                        {s.command && (
                          <div className="mt-2 font-mono text-xs bg-slate-900 text-emerald-300 rounded px-3 py-2 select-all">
                            $ {s.command}
                          </div>
                        )}
                        {s.risk_note && (
                          <p className="text-[11px] text-gray-500 mt-2 italic">
                            <strong className="not-italic">Risk:</strong> {s.risk_note}
                          </p>
                        )}
                        {s.action_id && (
                          <div className="mt-3">
                            <Button
                              size="sm"
                              onClick={() => runDepsAction(s.action_id, s.title)}
                              disabled={depsActionId !== null}
                              className="bg-orange-600 hover:bg-orange-700 text-white"
                              data-testid={`run-deps-action-${s.action_id}`}
                            >
                              {depsActionId === s.action_id ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Running…</>
                              ) : (
                                <>Run this now</>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDepsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cleanup Confirmation Dialog */}
      <Dialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Cleanup
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to proceed with this cleanup action?
            </DialogDescription>
          </DialogHeader>
          
          {selectedCleanup && (
            <div className="py-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-800">{selectedCleanup.name}</h4>
                <p className="text-sm text-red-600 mt-1">{selectedCleanup.description}</p>
                <p className="text-sm font-medium text-red-800 mt-2">
                  Estimated space to be freed: {selectedCleanup.estimated_savings}
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                This action cannot be undone. Please make sure you have backups if needed.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCleanupDialog(false);
              setSelectedCleanup(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleCleanup(selectedCleanup?.id)}
              className="bg-red-600 hover:bg-red-700"
              disabled={cleaning}
            >
              {cleaning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Cleaning...
                </>
              ) : (
                'Proceed with Cleanup'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorageMonitor;
